import styled from "styled-components/native";
import React from "react";
import { VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTheme } from "styled-components/native";
import RadioButton from "@components/atoms/RadioButton";
import { CardProps, PriceModeCardProps } from "./types";
import { TouchableOpacity } from "react-native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import ChangeMarkupForProduct from "./ChangePriceMarkup";
import { useTranslation } from "react-i18next";
import { VDBIcons } from "@utils/VDBIcons";
import { getBoxShadow } from "@utils/product/productValidators";
import { css } from "styled-components/native";

const PriceModeCard = ({
  title,
  description,
  checked,
  onPress,
  labelColor,
  isOrange,
  closeModal,
  setToastMessage,
}: PriceModeCardProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const userInfo = useUserInfo();
  const { t } = useTranslation();

  return (
    <Card
      backgroundColor={checked ? theme["core-01"] : theme["core-02"]}
      borderColor={checked ? labelColor : theme["core-04"]}
      isChecked={checked}
      onPress={onPress}
      disabled={checked}
    >
      {!(
        userInfo.userCanSetMarkupInOrangeMode() && userInfo.isPriceModeLock()
      ) && (
        <>
          <Container>
            <CardMain>
              <RadioButton
                hideRadioButton={theme.isLargeScreen && isOrange && checked}
                label={title}
                checked={checked}
                onPress={onPress}
                labelColor={labelColor}
                styles={checked ? typography["H3-M"] : typography["H3-R"]}
                margin="0"
              />
              {checked && (
                <ActiveView>
                  <VDBText
                    type={typography["Minor-R"]}
                    color={theme["core-05"]}
                  >
                    {t("priceMode.active.text")}
                  </VDBText>
                  <VDBIcon
                    variant={IconsVariant.VDBCustom}
                    name={VDBIcons.CHECK}
                    color={labelColor}
                    size={SpaceType.Big}
                  />
                </ActiveView>
              )}
            </CardMain>
            {description && (
              <VDBText
                type={checked ? typography["Minor-M"] : typography["Minor-R"]}
                color={checked ? theme["core-05"] : theme["core-06"]}
              >
                {description}
              </VDBText>
            )}
          </Container>
        </>
      )}
      {isOrange &&
        checked &&
        ((userInfo.userCanSetMarkupInOrangeMode() &&
          userInfo.isPriceModeLock()) ||
          (!userInfo.removeUserLevelMarkUpBox() &&
            userInfo.priceProfileVisibility())) && (
          <ChangeMarkupForProduct
            closeModal={() => {
              closeModal?.();
            }}
            setToastMessage={theme.isLargeScreen ? setToastMessage : undefined}
          />
        )}
    </Card>
  );
};

const CardMain = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Container = styled.View`
  margin: ${SpaceType.Double}px;
  gap: 8px;
`;

const Card = styled(TouchableOpacity)<CardProps>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: ${SpaceType.Basic}px;
  border-color: ${({ borderColor }) => borderColor};
  border-width: 1px;
  ${({ isChecked, theme }) =>
    isChecked &&
    css`
      box-shadow: ${getBoxShadow(theme.Shadows.Gentle)};
    `}
`;

const ActiveView = styled.View`
  flex-direction: row;
  align-items: center;
`;

export default PriceModeCard;
